import {
  buildOptionSlotSources,
  OptionCandidateInput,
  OptionMarkerInput,
  SECTION_NAME_RE,
  SLOT_NAME_RE,
} from './maOptionSlots';
import { MaCsvRow, MaParseResult, MaRulerLane, MaTimeValue, MaWarning } from './maTypes';

/**
 * Reaper プロジェクトファイル（.RPP）のパーサ。
 *
 * RPP を CSV と同等のシーケンス行（MaCsvRow[]）へ変換する。取得対象:
 * - ビデオ行: `<SOURCE VIDEO` を持つトラックアイテム
 *   （media = FILE のベース名 / start = POSITION / end = POSITION + LENGTH /
 *     offset = SOFFS）。同一トラック・同一ソースで隙間なく連続し、
 *   ソースオフセットも通し再生と一致する分割（純粋なカット割り）は 1 行にマージ。
 *   オフセット不連続な分割（ジャンプカット / リピート演出）は行を分けたまま出力し、
 *   リンク時に 1 セグメントの動画区間（baseVideoSpans）へ統合される
 * - オーディオ行: プロジェクト直下のリージョン / マーカー
 *   （media = リージョン名 / start・end = 区間。マーカーは end なし）
 * - track: リージョンは Render Track List（`<REGIONRENDER` の先頭 TRACK GUID）
 *   のトラック名（文字列）。ポイントマーカーは名前付きルーラーレーン
 *   （REAPER 7 の RULERLANE。Region/Marker Manager の「Ruler Lane」列）の
 *   レーン名をトラック名として設定。いずれも未指定は自動配置。
 *   同一トラックの後着優先カット（上書きで前の音を止める）をマーカー音にも
 *   効かせるには、レーン名をトラック名と同じにして割り当てる
 * - オーディオトラックのアイテム（`<SOURCE WAVE` 等）は取得しない
 *
 * オプションマーカー（`(x)` / `[x]` ポイントマーカー、詳細は maOptionSlots.ts）:
 * - `(x)` = オプション差し込みスロット。`[x]` セクションの候補を配置する
 *   （`(x=候補キー)` で初期選択を指定できる）
 * - 最初の `[` マーカー以降は「オプション候補置き場（パレット領域）」として
 *   本編行から除外される。各セクションは次の `[` マーカーまで
 * - セクション内で同じ開始位置に並ぶリージョン / 通常マーカー群（SE + VC 等）が
 *   1 候補（マーカーは end なし = ファイル末尾まで再生）。
 *   候補行はクラスタ先頭を 0 とする相対時刻で optionSlots に格納される
 * - パレット領域の動画アイテムは「候補クラスタの差し替え動画」として
 *   候補行に取り込む（本編行にはしない。種別判定はリンク時の file_type）。
 *   ミュートされたアイテム（ITEM の MUTE）/ ミュートトラック（MUTESOLO）上の
 *   ものは REAPER 用の参考スニペット扱いで候補にしない
 *
 * 時刻はすべて秒の実数値（MaTimeValue kind: 'seconds'）で返す。
 * 行番号（line）は RPP ファイル上の実行番号（警告表示・同時開始の
 * タイブレークに使用）。
 */

// ===== RPP ツリー構造 =====

/** RPP のブロックノード（`<TAG ...` 〜 `>`） */
interface RppNode {
  tag: string;
  /** 開始行の残りトークン（例: TRACK の GUID） */
  params: string[];
  /** 直下の属性行（トークン列 + 行番号） */
  attrs: Array<{ line: number; tokens: string[] }>;
  children: RppNode[];
  /** 開始行番号（1 始まり） */
  line: number;
}

/**
 * RPP 1 行のトークン分割。
 * Reaper は空白を含む文字列を `"..."` / `'...'` / `` `...` `` のいずれかで
 * 囲む（内部エスケープなし・囲み文字は値に含まれない文字が選ばれる）。
 */
export function tokenizeRppLine(line: string): string[] {
  const tokens: string[] = [];
  const n = line.length;
  let i = 0;
  while (i < n) {
    while (i < n && (line[i] === ' ' || line[i] === '\t')) i++;
    if (i >= n) break;
    const ch = line[i];
    if (ch === '"' || ch === "'" || ch === '`') {
      i++;
      const start = i;
      while (i < n && line[i] !== ch) i++;
      tokens.push(line.slice(start, i));
      i++; // 閉じ引用符
    } else {
      const start = i;
      while (i < n && line[i] !== ' ' && line[i] !== '\t') i++;
      tokens.push(line.slice(start, i));
    }
  }
  return tokens;
}

/** RPP テキストをブロックツリーに変換（ルート = REAPER_PROJECT） */
function parseRppTree(text: string): RppNode | null {
  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\r|\n/);
  let root: RppNode | null = null;
  const stack: RppNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const lineNo = i + 1;
    const trimmed = lines[i].trim();
    if (!trimmed) continue;

    if (trimmed.startsWith('<')) {
      const tokens = tokenizeRppLine(trimmed.slice(1));
      const node: RppNode = {
        tag: tokens[0] ?? '',
        params: tokens.slice(1),
        attrs: [],
        children: [],
        line: lineNo,
      };
      const parent = stack[stack.length - 1];
      if (parent) {
        parent.children.push(node);
      } else if (!root) {
        root = node;
      }
      stack.push(node);
      continue;
    }

    if (trimmed === '>') {
      stack.pop();
      continue;
    }

    const parent = stack[stack.length - 1];
    if (parent) {
      parent.attrs.push({ line: lineNo, tokens: tokenizeRppLine(trimmed) });
    }
  }

  return root;
}

// ===== 抽出ヘルパー =====

const seconds = (value: number): MaTimeValue => ({ kind: 'seconds', seconds: value });

/** ノード直下の属性行から最初の一致を返す */
function findAttr(node: RppNode, key: string): string[] | null {
  const attr = node.attrs.find(a => a.tokens[0] === key);
  return attr ? attr.tokens : null;
}

/** パス文字列のベース名（"Media/foo.wav" → "foo.wav"） */
function baseName(path: string): string {
  const normalized = path.replace(/\\/g, '/');
  const idx = normalized.lastIndexOf('/');
  return idx >= 0 ? normalized.slice(idx + 1) : normalized;
}

function parseFloatToken(tokens: string[] | null): number | null {
  if (!tokens || tokens[1] === undefined) return null;
  const value = parseFloat(tokens[1]);
  return Number.isFinite(value) ? value : null;
}

/**
 * MARKER 行のルーラーレーン番号を返す（REAPER 7 形式: GUID トークンの 2 つ後ろ）。
 * 旧形式（GUID やレーントークンがない）や不正値は null = レーン情報なし
 */
export function markerRulerLane(tokens: string[]): number | null {
  const guidIdx = tokens.findIndex(t => t.startsWith('{') && t.endsWith('}'));
  if (guidIdx < 0) return null;
  const laneToken = tokens[guidIdx + 2];
  if (laneToken === undefined) return null;
  const lane = parseInt(laneToken, 10);
  return Number.isFinite(lane) && lane > 0 ? lane : null;
}

/**
 * REAPER の色整数 → `#rrggbb`。
 * REAPER は「カスタム色あり」を 0x1000000 ビットで表し、下位 24bit は
 * Windows COLORREF（0x00BBGGRR = 下位バイトから R, G, B）。
 * 0 / フラグなし（= 既定色のまま）は null
 */
export function reaperColorToHex(value: number): string | null {
  if (!Number.isFinite(value) || (value & 0x1000000) === 0) return null;
  const r = value & 0xff;
  const g = (value >> 8) & 0xff;
  const b = (value >> 16) & 0xff;
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/**
 * MARKER 行の個別色（`#rrggbb`）。トークン位置は
 * [MARKER, id, position, name, flags, color, ...]（旧形式で欠落時は null）
 */
function markerColorHex(tokens: string[]): string | null {
  const raw = tokens[5];
  if (raw === undefined) return null;
  const value = parseInt(raw, 10);
  return Number.isFinite(value) ? reaperColorToHex(value) : null;
}

/**
 * SOURCE ノードからビデオソースを解決する。
 *
 * トリム済みアイテムは `<SOURCE SECTION` が実ソースをラップするため、
 * 内側の SOURCE を再帰解決し、STARTPOS をファイル内オフセットへ加算する
 * （実効オフセット = ITEM の SOFFS + 各 SECTION の STARTPOS）。
 * ビデオ以外のソース（WAVE 等）は null。
 */
function resolveVideoSource(source: RppNode): { node: RppNode; extraOffset: number } | null {
  if (source.params[0] === 'VIDEO') return { node: source, extraOffset: 0 };
  if (source.params[0] === 'SECTION') {
    const inner = source.children.find(c => c.tag === 'SOURCE');
    if (!inner) return null;
    const resolved = resolveVideoSource(inner);
    if (!resolved) return null;
    const startPos = parseFloatToken(findAttr(source, 'STARTPOS')) ?? 0;
    return { node: resolved.node, extraOffset: startPos + resolved.extraOffset };
  }
  return null;
}

/**
 * 同一ソースの分割動画アイテムを「連続」とみなす隙間・オフセットずれの
 * 許容値（秒。約 1.5 フレーム @30fps。カット位置の浮動小数誤差を吸収する）
 */
const VIDEO_MERGE_GAP_EPSILON = 0.05;

/** マージ前のビデオアイテム 1 個ぶんの中間表現 */
interface RppVideoItem {
  line: number;
  /** FILE の生パス（ベース名でなく生値で比較し、別ディレクトリの同名を誤結合しない） */
  file: string;
  position: number;
  /** POSITION + LENGTH（LENGTH 未取得・0 以下は null = 終端不明） */
  end: number | null;
  /** 実効ソースオフセット（SOFFS + SECTION の STARTPOS） */
  offset: number;
}

/**
 * 同一トラック内のビデオアイテムを「同一ソースで隙間なく連続するグループ」に
 * まとめる（グループ = マージ後の 1 行）。end はグループ内の最遅終了。
 * 終端不明のアイテムを取り込んだら end = null とし、そこで打ち切る。
 */
function groupAdjacentVideoItems(
  videoItems: RppVideoItem[],
): Array<{ items: RppVideoItem[]; end: number | null }> {
  const itemsByFile = new Map<string, RppVideoItem[]>();
  videoItems.forEach(v => {
    const arr = itemsByFile.get(v.file) || [];
    arr.push(v);
    itemsByFile.set(v.file, arr);
  });

  const groups: Array<{ items: RppVideoItem[]; end: number | null }> = [];
  itemsByFile.forEach(fileItems => {
    fileItems.sort((a, b) => a.position - b.position || a.line - b.line);

    let current: RppVideoItem[] = [];
    let currentEnd: number | null = null;
    const closeCurrent = () => {
      if (current.length > 0) groups.push({ items: current, end: currentEnd });
      current = [];
      currentEnd = null;
    };
    fileItems.forEach(v => {
      const adjacent =
        current.length > 0 &&
        currentEnd !== null &&
        v.position <= currentEnd + VIDEO_MERGE_GAP_EPSILON;
      if (!adjacent) closeCurrent();
      currentEnd =
        current.length === 0 || currentEnd === null || v.end === null
          ? v.end
          : Math.max(currentEnd, v.end);
      current.push(v);
    });
    closeCurrent();
  });
  return groups;
}

/**
 * 同一開始クラスタ内の候補行の並び（先頭行 = 候補ラベル）。
 * メモ用マーカーや差し替え動画の名前が候補ラベルにならないようにする
 */
const CANDIDATE_CLUSTER_ORDER = { region: 0, marker: 1, video: 2 } as const;

/** 確定済みリージョン（開閉ペア解決後） */
interface RppRegion {
  line: number;
  name: string;
  start: number;
  /** 終端行が見つからない場合は null（末尾まで再生） */
  end: number | null;
  /** Render Track List のトラック名 */
  track: string | null;
  /** リージョン個別色（#rrggbb。未設定は null → リンク時にレーン色フォールバック） */
  color: string | null;
}

/** 名前付きポイントマーカー */
interface RppPointMarker {
  line: number;
  name: string;
  position: number;
  /** 名前付きルーラーレーンのレーン名（トラックとして扱う。レーンなし / 無名は null） */
  track: string | null;
  /** マーカー個別色（#rrggbb。未設定は null → リンク時にレーン色フォールバック） */
  color: string | null;
}

/** オプションマーカー（`(x)` / `[x]`）に分類されたポイントマーカー */
interface RppOptionMarker {
  line: number;
  /** 括弧の内側（`(x=候補)` の `x=候補` / `[x]` の `x`） */
  inner: string;
  position: number;
}

// ===== 本体 =====

export function parseRppProject(text: string): MaParseResult {
  const warnings: MaWarning[] = [];
  const rows: MaCsvRow[] = [];

  const root = parseRppTree(text);
  if (!root || root.tag !== 'REAPER_PROJECT') {
    warnings.push({ line: null, message: 'Reaper プロジェクト（REAPER_PROJECT）として解釈できません' });
    return { rows, warnings, optionSlots: [] };
  }

  // ---- トラック GUID → トラック名（Render Track List の解決用） ----
  const trackNameByGuid = new Map<string, string>();
  const tracks = root.children.filter(c => c.tag === 'TRACK');
  tracks.forEach(track => {
    const guid = track.params[0] ?? findAttr(track, 'TRACKID')?.[1];
    const name = findAttr(track, 'NAME')?.[1] ?? '';
    if (guid) trackNameByGuid.set(guid, name);
  });

  // ---- Render Track List（REGIONRENDER: リージョン id → トラック名） ----
  const renderTrackByRegionId = new Map<string, string>();
  root.children
    .filter(c => c.tag === 'REGIONRENDER')
    .forEach(rr => {
      const regionId = rr.params[0];
      if (regionId === undefined) return;
      const trackAttrs = rr.attrs.filter(a => a.tokens[0] === 'TRACK');
      if (trackAttrs.length === 0) return;
      if (trackAttrs.length > 1) {
        warnings.push({
          line: rr.line,
          message: `リージョン ${regionId} の Render Track List に複数トラックが指定されています（先頭のみ使用）`,
        });
      }
      const guid = trackAttrs[0].tokens[1];
      const name = guid ? trackNameByGuid.get(guid) : undefined;
      if (!name) {
        warnings.push({
          line: trackAttrs[0].line,
          message: `リージョン ${regionId} の Render Track List のトラックが見つかりません`,
        });
        return;
      }
      renderTrackByRegionId.set(regionId, name);
    });

  // ---- ルーラーレーン（RULERLANE: [RULERLANE, レーン番号, flags, 名前, 色, ...]） ----
  // REAPER 7 のマーカー/リージョンレーン。Region/Marker Manager の
  // 「Ruler Lane」列で割り当てる。名前付きレーン上のポイントマーカーは
  // そのレーン名をトラックとして扱う（マーカーにはリージョンレンダー
  // マトリクスのような割当手段がないため、レーンがトラック指定の正になる）。
  // レーン色はクリップ色のフォールバック、レーン順はトラックの表示順になる
  const rulerLaneNames = new Map<number, string>();
  const rulerLanes: MaRulerLane[] = [];
  root.attrs
    .filter(a => a.tokens[0] === 'RULERLANE')
    .forEach(({ tokens }) => {
      const index = parseInt(tokens[1] ?? '', 10);
      const name = tokens[3] ?? '';
      if (!Number.isFinite(index) || index <= 0 || !name) return;
      if (rulerLaneNames.has(index)) return;
      rulerLaneNames.set(index, name);
      const colorValue = parseInt(tokens[4] ?? '', 10);
      rulerLanes.push({
        index,
        name,
        colorHex: Number.isFinite(colorValue) ? reaperColorToHex(colorValue) : null,
      });
    });
  rulerLanes.sort((a, b) => a.index - b.index);

  // ---- リージョン / ポイントマーカーの収集（プロジェクト直下の MARKER 行） ----
  // MARKER 行: [MARKER, id, position, name, flags, ...]
  //   flags bit0 = リージョン境界。同一 id の開閉ペアで 1 リージョン
  //   （開始行に名前、終端行は名前が空）。bit0 なしは単独のポイントマーカー
  const regions: RppRegion[] = [];
  const pointMarkers: RppPointMarker[] = [];
  const openRegions = new Map<
    string,
    { name: string; start: number; line: number; color: string | null }
  >();
  root.attrs
    .filter(a => a.tokens[0] === 'MARKER')
    .forEach(({ line, tokens }) => {
      const id = tokens[1];
      const position = parseFloat(tokens[2] ?? '');
      if (id === undefined || !Number.isFinite(position)) {
        warnings.push({ line, message: 'MARKER 行を解釈できないためスキップしました' });
        return;
      }
      const name = tokens[3] ?? '';
      const flags = parseInt(tokens[4] ?? '0', 10) || 0;
      const isRegion = (flags & 1) === 1;

      if (!isRegion) {
        // ポイントマーカー（名前なしはスキップ）。
        // 名前付きルーラーレーン上のマーカーはレーン名をトラックとして扱う
        if (name) {
          const lane = markerRulerLane(tokens);
          const track = lane !== null ? (rulerLaneNames.get(lane) ?? null) : null;
          pointMarkers.push({ line, name, position, track, color: markerColorHex(tokens) });
        }
        return;
      }

      const open = openRegions.get(id);
      if (!open) {
        openRegions.set(id, { name, start: position, line, color: markerColorHex(tokens) });
        return;
      }
      // リージョン確定（終端行の名前は空が正。色は開始行の値を使う）
      openRegions.delete(id);
      if (!open.name) return; // 名前なしリージョンはリンク先がないためスキップ
      regions.push({
        line: open.line,
        name: open.name,
        start: open.start,
        end: position,
        track: renderTrackByRegionId.get(id) ?? null,
        color: open.color,
      });
    });

  // 終端行が見つからなかったリージョンは end なしとして拾う
  openRegions.forEach((open, id) => {
    if (!open.name) return;
    warnings.push({
      line: open.line,
      message: `リージョン「${open.name}」の終端が見つかりません（末尾まで再生します）`,
    });
    regions.push({
      line: open.line,
      name: open.name,
      start: open.start,
      end: null,
      track: renderTrackByRegionId.get(id) ?? null,
      color: open.color,
    });
  });

  // ---- ポイントマーカーの分類（(x) スロット / [x] セクション / 通常） ----
  const slotMarkers: RppOptionMarker[] = [];
  const sectionMarkers: RppOptionMarker[] = [];
  const normalMarkers: RppPointMarker[] = [];
  pointMarkers.forEach(marker => {
    const slotMatch = marker.name.match(SLOT_NAME_RE);
    if (slotMatch) {
      slotMarkers.push({ line: marker.line, inner: slotMatch[1], position: marker.position });
      return;
    }
    const sectionMatch = marker.name.match(SECTION_NAME_RE);
    if (sectionMatch) {
      sectionMarkers.push({ line: marker.line, inner: sectionMatch[1], position: marker.position });
      return;
    }
    normalMarkers.push(marker);
  });

  // パレット領域（オプション候補置き場）= 最初の [ マーカー以降すべて
  const paletteStart =
    sectionMarkers.length > 0 ? Math.min(...sectionMarkers.map(s => s.position)) : Infinity;

  // ---- ビデオ行（<SOURCE VIDEO を持つトラックアイテム） ----
  // 同一トラック・同一ソースで隙間なく連続するアイテム群（Reaper 上のカット分割 /
  // リピート演出）をグループ化し、ソースオフセットが通し再生と連続する並びごとに
  // 1 行へまとめる。隣接した行はリンク時に 1 セグメントへ統合されるため、
  // 分割がサイドバーの重複サムネイルやオーディオ行の帰属分断になることはない
  const paletteVideoCandidates: OptionCandidateInput[] = [];
  /** ビデオアイテムを持つ最初のトラックの名前（タイムラインの MOV ラベル差し替え用） */
  let videoTrackName: string | null = null;
  tracks.forEach(track => {
    const mainVideoItems: RppVideoItem[] = [];
    const paletteVideoItems: RppVideoItem[] = [];
    // トラックミュート（MUTESOLO の第 1 値）。参考用トラックの判定に使う
    const trackMuted = parseFloatToken(findAttr(track, 'MUTESOLO')) === 1;
    track.children
      .filter(c => c.tag === 'ITEM')
      .forEach(item => {
        // 複数テイクは非対応: 先頭の SOURCE のみ見る（VIDEO 以外 = オーディオ等は対象外。
        // SECTION ラップは resolveVideoSource で内側の VIDEO を解決）
        const source = item.children.find(c => c.tag === 'SOURCE');
        const resolved = source ? resolveVideoSource(source) : null;
        if (!resolved) return;

        const file = findAttr(resolved.node, 'FILE')?.[1];
        if (!file) {
          warnings.push({ line: item.line, message: 'ビデオアイテムに FILE がないためスキップしました' });
          return;
        }
        const position = parseFloatToken(findAttr(item, 'POSITION'));
        if (position === null) {
          warnings.push({
            line: item.line,
            message: `ビデオアイテム「${baseName(file)}」の POSITION を解釈できないためスキップしました`,
          });
          return;
        }
        const length = parseFloatToken(findAttr(item, 'LENGTH'));
        const soffs = (parseFloatToken(findAttr(item, 'SOFFS')) ?? 0) + resolved.extraOffset;
        // パレット領域の動画は本編行にせず、候補クラスタの差し替え動画として集める。
        // ミュートされたアイテム（MUTE の第 1 値）/ ミュートトラック上のものは
        // REAPER 用の参考スニペット扱いで候補にもしない（削除せず残せるように）
        if (position >= paletteStart) {
          const itemMuted = parseFloatToken(findAttr(item, 'MUTE')) === 1;
          if (itemMuted || trackMuted) return;
        }
        (position >= paletteStart ? paletteVideoItems : mainVideoItems).push({
          line: item.line,
          file,
          position,
          end: length !== null && length > 0 ? position + length : null,
          offset: soffs,
        });
      });

    // 本編のビデオアイテムを持つ最初のトラック名を videoTrackName として採用
    if (videoTrackName === null && mainVideoItems.length > 0) {
      const name = findAttr(track, 'NAME')?.[1]?.trim();
      if (name) videoTrackName = name;
    }

    groupAdjacentVideoItems(mainVideoItems).forEach(({ items: groupItems }) => {
      // ソースオフセットが通し再生と連続する並びだけを 1 行にマージする。
      // 不連続な分割（リピート / ジャンプカット）は行を分けたまま出力し、
      // リンク時に同一セグメント内の動画区間（baseVideoSpans）として復元する
      //（linkMaSequence の隣接行統合。ファイル時刻どおりに再生される）
      const runs: RppVideoItem[][] = [];
      groupItems.forEach(v => {
        const run = runs[runs.length - 1];
        const prev = run?.[run.length - 1];
        const continuous =
          prev !== undefined &&
          Math.abs(v.offset - (prev.offset + (v.position - prev.position))) <=
            VIDEO_MERGE_GAP_EPSILON;
        if (continuous) run.push(v);
        else runs.push([v]);
      });
      runs.forEach(runItems => {
        const first = runItems[0];
        const end = runItems.some(v => v.end === null)
          ? null
          : Math.max(...runItems.map(v => v.end as number));
        rows.push({
          line: first.line,
          media: baseName(first.file),
          start: seconds(first.position),
          end: end !== null ? seconds(end) : null,
          offset: first.offset > 0 ? seconds(first.offset) : null,
          track: null,
          gainDb: null,
        });
      });
    });

    // パレット動画も分割配置を同様にマージし、1 グループ = 1 候補行にする
    //（開始位置一致で音声クラスタへ合流する。分割警告は参考配置のため出さない）
    groupAdjacentVideoItems(paletteVideoItems).forEach(({ items: groupItems, end }) => {
      const first = groupItems[0];
      paletteVideoCandidates.push({
        line: first.line,
        media: baseName(first.file),
        start: seconds(first.position),
        end: end !== null ? seconds(end) : null,
        offset: first.offset > 0 ? seconds(first.offset) : null,
        track: null,
        gainDb: null,
        clusterOrder: CANDIDATE_CLUSTER_ORDER.video,
        isVideo: true,
      });
    });
  });

  // ---- 本編のオーディオ行（パレット領域より前のリージョン / 通常マーカー） ----
  const paletteRegions: RppRegion[] = [];
  regions.forEach(region => {
    if (region.start >= paletteStart) {
      paletteRegions.push(region);
      return;
    }
    rows.push({
      line: region.line,
      media: region.name,
      start: seconds(region.start),
      end: region.end !== null ? seconds(region.end) : null,
      offset: null,
      track: region.track,
      gainDb: null,
      color: region.color,
    });
  });

  // 通常マーカー: 開始のみ（end なし = ファイル末尾まで再生）。
  // track は名前付きルーラーレーンのレーン名（なしは自動配置）。
  // パレット領域内のものはリージョンと同じくオプション候補行にする
  const paletteMarkers: RppPointMarker[] = [];
  normalMarkers.forEach(marker => {
    if (marker.position >= paletteStart) {
      paletteMarkers.push(marker);
      return;
    }
    rows.push({
      line: marker.line,
      media: marker.name,
      start: seconds(marker.position),
      end: null,
      offset: null,
      track: marker.track,
      gainDb: null,
      color: marker.color,
    });
  });

  // ---- オプションスロットの構築（共有ロジック: maOptionSlots.ts） ----
  const toOptionMarkerInput = (m: RppOptionMarker): OptionMarkerInput => ({
    line: m.line,
    inner: m.inner,
    start: seconds(m.position),
  });
  const regionToCandidate = (r: RppRegion): OptionCandidateInput => ({
    line: r.line,
    media: r.name,
    start: seconds(r.start),
    end: r.end !== null ? seconds(r.end) : null,
    offset: null,
    track: r.track,
    gainDb: null,
    color: r.color,
    clusterOrder: CANDIDATE_CLUSTER_ORDER.region,
  });
  const pointMarkerToCandidate = (m: RppPointMarker): OptionCandidateInput => ({
    line: m.line,
    media: m.name,
    start: seconds(m.position),
    end: null,
    offset: null,
    track: m.track,
    gainDb: null,
    color: m.color,
    clusterOrder: CANDIDATE_CLUSTER_ORDER.marker,
  });
  const optionSlots = buildOptionSlotSources({
    slotMarkers: slotMarkers.map(toOptionMarkerInput),
    sectionMarkers: sectionMarkers.map(toOptionMarkerInput),
    candidates: [
      ...paletteRegions.map(regionToCandidate),
      ...paletteMarkers.map(pointMarkerToCandidate),
      ...paletteVideoCandidates,
    ],
    warnings,
  });

  if (rows.length === 0) {
    warnings.push({ line: null, message: 'RPP からシーケンス行を抽出できませんでした' });
  }

  // 行番号順に整列（ソースファイル上の出現順）
  rows.sort((a, b) => a.line - b.line);

  return { rows, warnings, optionSlots, rulerLanes, videoTrackName };
}
